import { useState, useRef, useEffect } from 'react';

import { fr } from 'date-fns/locale';
import DatePicker, { registerLocale } from 'react-datepicker';

import 'react-datepicker/dist/react-datepicker.css';
import { BasicInput } from './BasicInput';
import { TotemLabel } from './TotemLabel';
import { TotemPrimaryButton } from './TotemPrimaryButton';

registerLocale('fr', fr);

type TotemDateRangePickerType = {
    label?: string;
    sublabel?: string;
    startDate: Date | null;
    endDate: Date | null;
    isDisabled?: boolean;
    shouldAllowPastDates?: boolean;
    onConfirm: (dates: { startDate: Date | null; endDate: Date | null }) => void;
};

export const TotemDateRangePicker = ({
    label,
    sublabel,
    startDate: initialStartDate,
    endDate: initialEndDate,
    isDisabled,
    shouldAllowPastDates = true,
    onConfirm,
}: TotemDateRangePickerType) => {
    const [startDate, setStartDate] = useState<Date | null>(initialStartDate);
    const [endDate, setEndDate] = useState<Date | null>(initialEndDate);
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const handleDateChange = (dates: any) => {
        if (!dates) {
            setStartDate(null);
            setEndDate(null);
            return;
        }
        if (Array.isArray(dates)) {
            const [start, end] = dates;
            setStartDate(start);
            setEndDate(end);
        } else {
            setStartDate(dates);
            setEndDate(null);
        }
    };

    const handleConfirm = () => {
        onConfirm({ startDate, endDate });
        setIsOpen(false);
    };

    const dateFormat = 'dd/MM/yyyy';

    const today = new Date();
    const isDateAfterToday = (date: Date) => {
        return date > today;
    };

    const displayValue = () => {
        if (startDate && endDate) {
            return `${startDate.toLocaleDateString('fr-FR')} - ${endDate.toLocaleDateString('fr-FR')}`;
        }
        if (startDate) {
            return startDate.toLocaleDateString('fr-FR');
        }
        return '';
    };

    return (
        <div className="flex flex-col w-full gap-[5px]">
            {label ? <TotemLabel>{label}</TotemLabel> : null}
            {sublabel ? <span className="text-muted-foreground text-[13px]">{sublabel}</span> : null}
            <div className="relative" ref={wrapperRef}>
                <BasicInput value={displayValue()} readOnly onClick={() => setIsOpen(!isOpen)} />
                {isOpen && (
                    <div className="absolute top-full mt-[5px] z-[1000] bg-background border border-border rounded-md p-2.5 shadow-md">
                        <DatePicker
                            selected={startDate}
                            onChange={handleDateChange}
                            startDate={startDate}
                            endDate={endDate}
                            selectsRange
                            locale="fr"
                            disabled={isDisabled}
                            dateFormat={dateFormat}
                            inline
                            monthsShown={1}
                            filterDate={(date) => shouldAllowPastDates || isDateAfterToday(date)}
                            shouldCloseOnSelect={false}
                        />
                        <div className="flex gap-2.5 justify-end pt-2.5">
                            <TotemPrimaryButton type="button" onClick={handleConfirm}>
                                Confirmer
                            </TotemPrimaryButton>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
